import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { AsyncHorizontalNavJira } from '@jira/navigation-apps__horizontal-nav-jira/src/async.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { createLayout } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import { issueNavigatorProjectSoftwareRoute } from '@jira/router-routes__issue-navigator-routes/src/issueNavigatorProjectSoftwareRoute.tsx';
import { ProjectIssueNavigatorSkeleton } from '@jira/platform__skeletons/src/ui/issue-navigator/components/project-app.tsx';
import { softwareProjectLayoutNoOnboarding } from '@jira/platform__layouts-software-project-layout/src/index.tsx';
import { NIN_NEW_ISSUE_NAVIGATOR } from '@jira/platform__observability-capabilities/src/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { Route } from '@atlassian/react-resource-router';
import { getCommonProjectResources } from './common/getCommonProjectResources.tsx';
import { getRouteReporting } from './common/getRouteReporting.tsx';
import { getSsrCircuitBreaker } from './common/getSsrCircuitBreaker.tsx';
import { adjustSsrFetchTimeout } from './common/getSsrFetchTimeout.tsx';
import {
	issueNavigatorPageEntryPoint,
	issueNavigatorPageEntryPointWithoutPreviewPanel,
} from './ui/index.tsx';
import { ListRedirect } from './ui/list-redirect/ListRedirect.tsx';

/**
 * Software Project Issue Navigator Route Entry
 * Handles Software projects.
 */
export const issueNavigatorProjectSoftwareRouteEntry: Route = createEntry(
	issueNavigatorProjectSoftwareRoute,
	() => ({
		group: 'issue-navigator',
		meta: {
			capability: NIN_NEW_ISSUE_NAVIGATOR,
			reporting: getRouteReporting(),
		},
		skeleton: ProjectIssueNavigatorSkeleton,
		navigation: {
			menuId: MenuIdProjects,
			horizontal: AsyncHorizontalNavJira,
		},
		resources: [...getCommonProjectResources()],
		layout: createLayout(softwareProjectLayoutNoOnboarding),
		bifrost: {
			bifrostGroup: 'issue-navigator',
			manifestName: 'jira-spa-issue-navigator.json',
			circuitBreakerConfig: getSsrCircuitBreaker(),
			// Experiment to test the impact of increasing the resource timeout for issue navigator routes with more performance SSR
			fetchTimeout: adjustSsrFetchTimeout(expVal('nin_ssr_resource_timeout', 'timeout', 0)),
		},

		// eslint-disable-next-line no-restricted-syntax
		component: fg('jira_list_nin_merge') ? ListRedirect : undefined,
		entryPoint: () => {
			if (fg('jira_list_nin_merge')) {
				return undefined;
			}
			return fg('nin_glance_panel_technical_changes')
				? issueNavigatorPageEntryPoint
				: issueNavigatorPageEntryPointWithoutPreviewPanel;
		},
		supportsPreviewPanel: true,
		supportsProjectTheme: expVal('jira-list-full-theming-exp', 'isEnabled', false)
			? 'full'
			: 'contoured',
		isHydrateRootEnabled: true,
		isStreamingEnabled: fg('jfp-magma-ssr-streaming-enable-nin'),
		excludeFromWrmDeferredLoading: true,
	}),
);
